import Reflux from "reflux";
import Immutable, { List } from "immutable";
import ElementUtils from "../utils/ElementUtils";
import ElementGroupUtils from "../utils/ElementGroupUtils";
import SelectionActions from "../actions/SelectionActions";
import BlockUtils from "../utils/BlockUtils";

const SelectionStore = Reflux.createStore({
  storeName: 'SelectionStore',
  listenables: [SelectionActions],

  async init() {
    // Dynamic import breaks circular dependency:
    // SelectionStore → ActiveBlockContainerStore → SiteDesignStore → SelectionStore
    // Static import would cause undefined exports at module initialization time.
    this.activeContainer = (await import("./ActiveBlockContainerStore.mjs")).default;
    this.listenTo(this.activeContainer, this.onContainerChange);

    this.elements = Immutable.Map();
    this.block = Immutable.Map();
    this.elementIds = [];
    this.fullSelectedElements = [];
    this.from = null;
    this.layout = null;
    this.dragSelecting = false;
    this.showSelectLayer = true;
    this.elementLookup = {};
  },

  /**
   * Handles container change action by updating selection state
   * @param {Immutable.Map} container - The container with selected block data
   * @listens SelectionActions.containerChange
   */
  onContainerChange() {
    const container = this.activeContainer.getContainer();

    this.block = container.get("selectedBlock");

    if (!this.block.isEmpty()) {
      var selection = this.block.get("selected");
      const containerId = this.block.get("id");
      const allElements = this.block.get("elements");
      const states = this.block.get("states", List());

      this.elementLookup = ElementGroupUtils.buildElementLookup(allElements, containerId);

      if (states.size > 0) {
        states.forEach((state, idx) => {
          const stateElements = state.get("elements");
          const stateKey = BlockUtils.getStateID(containerId, idx);
          const lookupItem = ElementGroupUtils.buildElementLookup(stateElements, stateKey);
          Object.assign(this.elementLookup, lookupItem);
        });
      }

      this.layout = selection.get("layout");
      this.from = selection.get("from");
      this.elements = selection.get("elements");
      this.elementIds = this.elements?.keySeq().toArray() || [];

      // Filter out any null/undefined elements to prevent race condition crashes
      this.fullSelectedElements = ElementUtils.getSelectedElements(this.block).filter((e) => {
        if (!e) {
          console.warn("[SelectionStore] Selected Element is undefined");
        }
        return e != null && e !== undefined;
      });
    } else {
      this.layout = null;
      this.from = null;
      this.block = this.block.clear();
      this.elements = this.elements.clear();
      this.elementIds = [];
      this.fullSelectedElements = [];
      this.elementLookup = {};
    }

    this.trigger();
  },

  /**
   * Toggles visibility of stage select layer
   * @listens SelectionActions.toggleStageSelectLayer
   */
  onToggleStageSelectLayer() {
    this.showSelectLayer = !this.showSelectLayer;
    this.trigger();
  },

  /**
   * Checks if there are any selected elements
   * @returns {boolean} True if elements are selected
   */
  hasSelectedElements() {
    return !this.elements.isEmpty();
  },

  /**
   * Gets the currently selected block
   * @returns {Immutable.Map} The selected block
   */
  getBlock() {
    return this.block;
  },

  /**
   * Gets the selected elements map
   * @returns {Immutable.Map} Map of selected elements
   */
  getElements() {
    return this.elements;
  },

  /**
   * Gets array of full selected element objects
   * @returns {Array<Immutable.Map>} Array of selected elements
   */
  getFullElements() {
    return this.fullSelectedElements;
  },

  /**
   * Gets array of full selected elements converted to JS objects
   * @returns {Array<Object>} Array of selected elements as plain objects
   */
  getFullElementsJS() {
    return this.getFullElements().map((e) => e.toJS());
  },

  /**
   * Gets array of selected elements filtered by type
   * @param {string} type - Element type to filter by
   * @returns {Array<Immutable.Map>} Filtered array of elements
   */
  getFullElementsByType(type) {
    return this.getFullElements().filter((e) => e.get("type") === type);
  },

  /**
   * Gets array of selected elements as JS objects filtered by type
   * @param {string} type - Element type to filter by
   * @returns {Array<Object>} Filtered array of element objects
   */
  getFullElementsByTypeJS(type) {
    return this.getFullElementsJS().filter((e) => e.type === type);
  },

  /**
   * Gets array of selected text elements
   * @returns {Array<Immutable.Map>} Array of text elements
   */
  getTextElements() {
    return this.getFullElements().filter((e) => e.get("type") === "text");
  },

  /**
   * Gets array of selected element IDs
   * @returns {Array<string>} Array of element IDs
   */
  getElementIds() {
    return this.elementIds;
  },

  /**
   * Gets visibility state of select layer
   * @returns {boolean} True if select layer is visible
   */
  getShowSelectLayer() {
    return this.showSelectLayer;
  },

  /**
   * Gets ID of single selected element if only one is selected
   * @returns {string|boolean} Element ID or false if multiple/none selected
   */
  getSelectedElementId() {
    const fullElements = this.getFullElements();
    if (fullElements.length === 1) {
      return fullElements[0].getIn(["selected", "id"]);
    }
    return false;
  },

  /**
   * Gets type of single selected element if only one is selected
   * @returns {string|boolean} Element type or false if multiple/none selected
   */
  getSelectedElementType() {
    const validElements = this.getFullElements();
    if (validElements.length === 1) {
      return validElements[0].get("type");
    } else return false;
  },

  /**
   * Gets array of selected text element IDs
   * @returns {Array<string>} Array of text element IDs
   */
  getTextElementIds() {
    return this.elementIds.filter((id) => ElementUtils.isTextElementById(id, this.block));
  },

  /**
   * Gets array of visible selected elements for given layout
   * @param {string} layout - Layout to filter visibility by
   * @returns {Array<Object>} Array of visible element objects
   */
  getFullVisibleElementsJS(layout) {
    const visibility = layout.length > 1 ? layout[0] : layout;
    return this.getFullElementsJS().filter(({ visible }) => ["a", visibility].includes(visible));
  },

  /**
   * Gets current layout
   * @returns {string} Current layout
   */
  getLayout() {
    return this.layout;
  },

  /**
   * Sets drag selecting state
   * @param {boolean} value - New drag selecting state
   * @listens SelectionActions.setDragSelecting
   */
  onSetDragSelecting(value) {
    this.dragSelecting = value;
  },

  /**
   * Gets current drag selecting state
   * @returns {boolean} Current drag selecting state
   */
  getDragSelectingState() {
    return this.dragSelecting;
  },

  /**
   * Gets the element lookup
   * @returns {Object} The element lookup
   */
  getElementLookup() {
    return this.elementLookup;
  },

  /**
   * Gets element by unique ID
   * @param {string} id - Unique ID of the element
   * @returns {Object|undefined} The element or undefined if not found
   */
  getElementByUniqueId(id) {
    return this.elementLookup[id];
  },
});

export default SelectionStore;
